import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  Clock,
  MapPin,
  LogIn,
  LogOut,
  Calendar,
  DollarSign,
  Navigation,
  CheckCircle,
  AlertCircle,
  Car,
  X,
  Coffee,
  Play,
  Pause,
  History,
  Settings,
  Download,
  AlertTriangle,
  Users,
  BarChart3,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { timeClockService, TimeEntry, Break } from '../utils/services/timeClockService';
import ManagerTimeClock from './ManagerTimeClock';

interface TimeClockProps {
  userId: string;
  userName: string;
  userRole?: string;
  companyId?: string;
  onClose: () => void;
  onClockStatusChange?: (isClockedIn: boolean) => void;
}

export default function TimeClock({
  userId,
  userName,
  userRole = 'pumper',
  companyId,
  onClose,
  onClockStatusChange
}: TimeClockProps) {
  // State
  const [showManagerView, setShowManagerView] = useState(false);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [currentEntry, setCurrentEntry] = useState<TimeEntry | null>(null);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mileage, setMileage] = useState('');
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'clock' | 'timesheet' | 'payroll' | 'settings'>('clock');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(null);

  // Payroll settings
  const [hourlyRate, setHourlyRate] = useState(25);
  const [overtimeRate, setOvertimeRate] = useState(1.5);
  const [mileageRate, setMileageRate] = useState(0.67);
  const [overtimeThreshold, setOvertimeThreshold] = useState(40);

  // Work schedule
  const [workSchedule, setWorkSchedule] = useState<{
    [key: number]: { start: string; end: string; enabled: boolean };
  }>({
    0: { start: '', end: '', enabled: false }, // Sunday
    1: { start: '06:00', end: '18:00', enabled: true },
    2: { start: '06:00', end: '18:00', enabled: true },
    3: { start: '06:00', end: '18:00', enabled: true },
    4: { start: '06:00', end: '18:00', enabled: true },
    5: { start: '06:00', end: '18:00', enabled: true },
    6: { start: '', end: '', enabled: false }, // Saturday
  });

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Load time entries and settings
  useEffect(() => {
    loadTimeEntries();
    checkActiveEntry();
    loadSettings();
  }, [userId]);

  const loadSettings = async () => {
    try {
      // Load payroll settings
      const payroll = await timeClockService.getPayrollSettings(userId);
      if (payroll) {
        setHourlyRate(payroll.hourlyRate);
        setOvertimeRate(payroll.overtimeRate);
        setMileageRate(payroll.mileageRate);
        setOvertimeThreshold(payroll.overtimeThreshold);
      }

      // Load work schedule
      const schedules = await timeClockService.getWorkSchedule(userId);
      if (schedules && schedules.length > 0) {
        // Convert array to object format for state
        const scheduleObj: { [key: number]: { start: string; end: string; enabled: boolean } } = {};
        schedules.forEach(s => {
          scheduleObj[s.dayOfWeek] = {
            start: s.startTime,
            end: s.endTime,
            enabled: s.isActive
          };
        });
        setWorkSchedule(scheduleObj);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadTimeEntries = async () => {
    try {
      const entries = await timeClockService.getUserTimeEntries(userId);
      setTimeEntries(entries);
    } catch (error) {
      console.error('Failed to load time entries:', error);
      // Fall back to localStorage
      const stored = localStorage.getItem(`time-entries-${userId}`);
      if (stored) {
        setTimeEntries(JSON.parse(stored));
      }
    }
  };

  const checkActiveEntry = async () => {
    try {
      const active = await timeClockService.getActiveEntry(userId);
      if (active) {
        setIsClockedIn(true);
        setCurrentEntry(active);
        onClockStatusChange?.(true);

        // Check if on break
        const activeBreak = active.breaks.find(b => !b.endTime);
        if (activeBreak) {
          setIsOnBreak(true);
          setBreakStartTime(new Date(activeBreak.startTime));
        }
      } else {
        onClockStatusChange?.(false);
      }
    } catch (error) {
    }
  };

  // Update elapsed time
  useEffect(() => {
    if (!isClockedIn || !currentEntry) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const clockInTime = new Date(currentEntry.clockIn).getTime();
      const elapsed = Math.floor((now - clockInTime) / 1000);
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isClockedIn, currentEntry]);

  // Get current location
  const getCurrentLocation = async (): Promise<{ lat: number; lng: number; address: string }> => {
    setGettingLocation(true);

    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setGettingLocation(false);
        resolve({ lat: 0, lng: 0, address: 'Location unavailable' });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          // Reverse geocode (simplified - in production use Google Maps API)
          let address = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

          try {
            // Try to get address from Google
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();
            if (data.results?.[0]?.formatted_address) {
              address = data.results[0].formatted_address;
            }
          } catch {
            // Use coordinates as fallback
          }

          setCurrentLocation({ lat, lng });
          setGettingLocation(false);
          resolve({ lat, lng, address });
        },
        (error) => {
          setGettingLocation(false);
          resolve({ lat: 0, lng: 0, address: 'Location unavailable' });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  // Clock In
  const handleClockIn = async () => {
    try {
      const location = await getCurrentLocation();

      const newEntry = await timeClockService.clockIn(userId, location, notes);

      setCurrentEntry(newEntry);
      setIsClockedIn(true);
      onClockStatusChange?.(true);

      // Also save to localStorage as backup
      const entries = [...timeEntries, newEntry];
      setTimeEntries(entries);
      localStorage.setItem(`time-entries-${userId}`, JSON.stringify(entries));

      toast.success('Clocked in successfully');
    } catch (error) {
      console.error('Clock in error:', error);
      toast.error('Failed to clock in');
    }
  };

  // Clock Out
  const handleClockOut = async () => {
    if (!currentEntry) return;

    try {
      // End any active break first
      if (isOnBreak) {
        await handleEndBreak();
      }

      const location = await getCurrentLocation();
      const mileageNum = parseFloat(mileage) || 0;

      const updatedEntry = await timeClockService.clockOut(
        userId,
        currentEntry.id,
        location,
        mileageNum,
        notes
      );

      const updatedEntries = timeEntries.map(e =>
        e.id === currentEntry.id ? updatedEntry : e
      );
      setTimeEntries(updatedEntries);
      localStorage.setItem(`time-entries-${userId}`, JSON.stringify(updatedEntries));

      setIsClockedIn(false);
      setCurrentEntry(null);
      setMileage('');
      setNotes('');
      setElapsedTime(0);
      onClockStatusChange?.(false);

      toast.success('Clocked out successfully', {
        description: `${updatedEntry.hoursWorked?.toFixed(2)} hours worked`,
      });
    } catch (error) {
      console.error('Clock out error:', error);
      toast.error('Failed to clock out');
    }
  };

  // Start Break
  const handleStartBreak = async (type: Break['type'] = 'lunch') => {
    if (!currentEntry) return;

    try {
      await timeClockService.startBreak(userId, currentEntry.id, type, type === 'short');
      setIsOnBreak(true);
      setBreakStartTime(new Date());

      toast.info(`${type === 'lunch' ? 'Lunch' : 'Short'} break started`);
    } catch (error) {
      console.error('Failed to start break:', error);
      toast.error('Failed to start break');
    }
  };

  // End Break
  const handleEndBreak = async () => {
    if (!currentEntry) return;

    try {
      await timeClockService.endBreak(userId, currentEntry.id);
      setIsOnBreak(false);
      setBreakStartTime(null);

      toast.success('Break ended');
    } catch (error) {
      console.error('Failed to end break:', error);
      toast.error('Failed to end break');
    }
  };

  // Format time
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timesheet stats
  const calculateTimesheetStats = () => {
    const today = new Date().toDateString();
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    weekStart.setHours(0, 0, 0, 0);

    const thisWeek = timeEntries.filter(entry => {
      const entryDate = new Date(entry.clockIn);
      return entryDate >= weekStart;
    });

    const todayEntries = timeEntries.filter(entry =>
      new Date(entry.clockIn).toDateString() === today
    );

    const totalHoursToday = todayEntries.reduce((sum, e) => sum + (e.hoursWorked || 0), 0);
    const totalHoursWeek = thisWeek.reduce((sum, e) => sum + (e.hoursWorked || 0), 0);
    const totalMileageWeek = thisWeek.reduce((sum, e) => sum + (e.mileage || 0), 0);

    // Calculate regular vs overtime
    const regularHours = Math.min(totalHoursWeek, overtimeThreshold);
    const overtimeHours = Math.max(0, totalHoursWeek - overtimeThreshold);

    // Calculate pay
    const regularPay = regularHours * hourlyRate;
    const overtimePay = overtimeHours * hourlyRate * overtimeRate;
    const mileagePay = totalMileageWeek * mileageRate;
    const totalPay = regularPay + overtimePay + mileagePay;

    return {
      todayHours: totalHoursToday,
      weekHours: totalHoursWeek,
      regularHours,
      overtimeHours,
      weekMileage: totalMileageWeek,
      weekEntries: thisWeek.length,
      regularPay,
      overtimePay,
      mileagePay,
      totalPay,
      pendingApproval: thisWeek.filter(e => e.status === 'pending_approval').length,
      approved: thisWeek.filter(e => e.status === 'approved').length,
    };
  };

  const stats = calculateTimesheetStats();

  // Generate shift report
  const generateShiftReport = () => {
    const today = new Date().toDateString();
    const todayEntries = timeEntries.filter(entry =>
      new Date(entry.clockIn).toDateString() === today && entry.clockOut
    );

    if (todayEntries.length === 0) {
      toast.info('No completed shifts today');
      return;
    }

    const totalHours = todayEntries.reduce((sum, e) => sum + (e.hoursWorked || 0), 0);
    const totalMileage = todayEntries.reduce((sum, e) => sum + (e.mileage || 0), 0);
    const wellsVisited = new Set(todayEntries.flatMap(e => e.wellsVisited));

    const report = `
DAILY SHIFT REPORT
Date: ${new Date().toLocaleDateString()}
Pumper: ${userName}

HOURS WORKED: ${totalHours.toFixed(2)} hours
MILEAGE: ${totalMileage.toFixed(1)} miles
WELLS CHECKED: ${wellsVisited.size}

SHIFT DETAILS:
${todayEntries.map((entry, i) => `
Shift ${i + 1}:
  Clock In: ${new Date(entry.clockIn).toLocaleTimeString()}
  Clock Out: ${entry.clockOut ? new Date(entry.clockOut).toLocaleTimeString() : 'N/A'}
  Duration: ${(entry.hoursWorked || 0).toFixed(2)} hours
  Mileage: ${(entry.mileage || 0).toFixed(1)} miles
  Notes: ${entry.notes || 'None'}
`).join('\n')}

ESTIMATED PAY:
  Regular: $${(Math.min(totalHours, 8) * hourlyRate).toFixed(2)}
  Overtime: $${(Math.max(0, totalHours - 8) * hourlyRate * overtimeRate).toFixed(2)}
  Mileage: $${(totalMileage * mileageRate).toFixed(2)}
  TOTAL: $${((Math.min(totalHours, 8) * hourlyRate) + (Math.max(0, totalHours - 8) * hourlyRate * overtimeRate) + (totalMileage * mileageRate)).toFixed(2)}
    `.trim();

    // Download report
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shift-report-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Shift report downloaded');
  };

  // Save payroll settings
  const savePayrollSettings = async () => {
    try {
      await timeClockService.savePayrollSettings(userId, {
        hourlyRate,
        overtimeRate,
        overtimeThreshold,
        mileageRate,
        payPeriod: 'weekly',
        payPeriodStart: 0
      });
      toast.success('Payroll settings saved');
    } catch (error) {
      console.error('Failed to save payroll settings:', error);
      toast.error('Failed to save settings');
    }
  };

  // Save work schedule
  const saveWorkSchedule = async () => {
    try {
      // Convert object format to array format for API
      const schedules = Object.entries(workSchedule)
        .filter(([_, schedule]) => schedule.enabled)
        .map(([dayOfWeek, schedule]) => ({
          dayOfWeek: parseInt(dayOfWeek),
          startTime: schedule.start,
          endTime: schedule.end,
          isActive: schedule.enabled
        }));

      await timeClockService.saveWorkSchedule(userId, schedules);
      toast.success('Work schedule saved');
    } catch (error) {
      console.error('Failed to save work schedule:', error);
      toast.error('Failed to save schedule');
    }
  };

  if (showManagerView) {
    return (
      <ManagerTimeClock
        managerId={userId}
        managerName={userName}
        companyId={companyId || ''}
        onClose={() => setShowManagerView(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white dark:bg-slate-950 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-4 shadow-lg flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="flex items-center space-x-2">
              <Clock className="h-6 w-6" />
              <h2 className="text-xl font-bold">Time Clock</h2>
            </div>
            <p className="text-sm text-blue-100 mt-1">{userName}</p>
          </div>

          <div className="flex items-center gap-2">
            {(userRole === 'manager' || userRole === 'admin' || userRole === 'superadmin') && (
              <Button
                onClick={() => setShowManagerView(true)}
                variant="secondary"
                size="sm"
                className="bg-white/10 hover:bg-white/20 text-white border-0"
              >
                <Users className="h-4 w-4 mr-2" />
                Manager Portal
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 h-10 w-10"
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center space-x-2">
          <Badge className={isClockedIn ? (isOnBreak ? 'bg-yellow-500' : 'bg-green-500') : 'bg-gray-500'}>
            {isOnBreak ? (
              <>
                <Coffee className="h-3 w-3 mr-1" />
                On Break
              </>
            ) : isClockedIn ? (
              <>
                <CheckCircle className="h-3 w-3 mr-1" />
                Clocked In
              </>
            ) : (
              <>
                <AlertCircle className="h-3 w-3 mr-1" />
                Clocked Out
              </>
            )}
          </Badge>

          {isClockedIn && (
            <span className="text-sm font-mono font-bold">
              {formatTime(elapsedTime)}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-50 dark:bg-slate-900 border-b dark:border-slate-800 flex-shrink-0">
        <div className="flex">
          {[
            { id: 'clock', label: 'Clock', icon: Clock },
            { id: 'timesheet', label: 'Timesheet', icon: Calendar },
            { id: 'payroll', label: 'Payroll', icon: DollarSign },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex-1 px-4 py-3 font-medium text-sm transition-colors ${activeTab === tab.id
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white dark:bg-slate-950'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <tab.icon className="h-4 w-4 mx-auto mb-1" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Clock Tab */}
        {activeTab === 'clock' && (
          <div className="p-4 space-y-4">
            {/* Current Status */}
            <Card className={isClockedIn ? 'border-2 border-green-500' : ''}>
              <CardContent className="p-6 text-center">
                {!isClockedIn ? (
                  <>
                    <Clock className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Ready to Start</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                      Tap the button below to clock in and start your shift
                    </p>

                    <Button
                      onClick={handleClockIn}
                      disabled={gettingLocation}
                      className="w-full h-16 text-lg bg-green-600 hover:bg-green-700"
                    >
                      {gettingLocation ? (
                        <>
                          <Navigation className="h-6 w-6 mr-2 animate-spin" />
                          Getting Location...
                        </>
                      ) : (
                        <>
                          <LogIn className="h-6 w-6 mr-2" />
                          Clock In
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="relative mb-6">
                      <div className={`w-32 h-32 mx-auto rounded-full flex items-center justify-center ${isOnBreak ? 'bg-yellow-100' : 'bg-green-100'
                        }`}>
                        <div className={`w-28 h-28 rounded-full flex items-center justify-center text-white ${isOnBreak ? 'bg-yellow-500' : 'bg-green-500'
                          }`}>
                          <div className="text-center">
                            {isOnBreak ? (
                              <Coffee className="h-8 w-8 mx-auto mb-2" />
                            ) : (
                              <Clock className="h-8 w-8 mx-auto mb-2" />
                            )}
                            <p className="text-2xl font-bold font-mono">
                              {formatTime(elapsedTime)}
                            </p>
                          </div>
                        </div>
                      </div>
                      {!isOnBreak && (
                        <div className="absolute inset-0 border-4 border-green-500 rounded-full animate-ping opacity-20"></div>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {isOnBreak ? 'On Break' : 'Shift in Progress'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                      Clocked in at {new Date(currentEntry!.clockIn).toLocaleTimeString()}
                    </p>

                    {/* Break Controls */}
                    {!isOnBreak ? (
                      <div className="flex gap-2 mb-4">
                        <Button
                          onClick={() => handleStartBreak('lunch')}
                          variant="outline"
                          className="flex-1"
                        >
                          <Coffee className="h-4 w-4 mr-2" />
                          Lunch Break
                        </Button>
                        <Button
                          onClick={() => handleStartBreak('short')}
                          variant="outline"
                          className="flex-1"
                        >
                          <Pause className="h-4 w-4 mr-2" />
                          Short Break
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={handleEndBreak}
                        className="w-full mb-4 bg-yellow-600 hover:bg-yellow-700"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        End Break
                      </Button>
                    )}

                    {/* Mileage */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                        Mileage (optional)
                      </label>
                      <div className="relative">
                        <Car className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          type="number"
                          value={mileage}
                          onChange={(e) => setMileage(e.target.value)}
                          placeholder="0.0"
                          className="pl-10 h-12"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                        Shift Notes (optional)
                      </label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any issues, observations, or important notes..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>

                    <Button
                      onClick={handleClockOut}
                      disabled={gettingLocation || isOnBreak}
                      className="w-full h-16 text-lg bg-red-600 hover:bg-red-700"
                    >
                      {gettingLocation ? (
                        <>
                          <Navigation className="h-6 w-6 mr-2 animate-spin" />
                          Getting Location...
                        </>
                      ) : (
                        <>
                          <LogOut className="h-6 w-6 mr-2" />
                          Clock Out
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Today</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.todayHours.toFixed(1)}</p>
                  <p className="text-xs text-gray-500">hours</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">This Week</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.weekHours.toFixed(1)}</p>
                  <p className="text-xs text-gray-500">hours</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Timesheet Tab */}
        {activeTab === 'timesheet' && (
          <div className="p-4 space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                <CardContent className="p-4 text-center">
                  <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.weekHours.toFixed(1)}</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">Hours This Week</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                <CardContent className="p-4 text-center">
                  <Car className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">{stats.weekMileage.toFixed(1)}</p>
                  <p className="text-xs text-green-700 dark:text-green-300">Miles This Week</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20">
                <CardContent className="p-4 text-center">
                  <AlertTriangle className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">{stats.pendingApproval}</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">Pending Approval</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{stats.approved}</p>
                  <p className="text-xs text-purple-700 dark:text-purple-300">Approved</p>
                </CardContent>
              </Card>
            </div>

            {/* Export Button */}
            <Button onClick={generateShiftReport} className="w-full" variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Download Today's Report
            </Button>

            {/* Time Entries List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <History className="h-4 w-4 mr-2" />
                  Recent Entries
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {timeEntries.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No time entries yet</p>
                  </div>
                ) : (
                  <div className="divide-y dark:divide-slate-700">
                    {timeEntries.slice().reverse().slice(0, 10).map((entry) => (
                      <div key={entry.id} className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {new Date(entry.clockIn).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(entry.clockIn).toLocaleTimeString()} - {' '}
                              {entry.clockOut ? new Date(entry.clockOut).toLocaleTimeString() : 'In progress'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge className={
                              entry.status === 'approved' ? 'bg-green-100 text-green-800' :
                                entry.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                                  entry.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    entry.status === 'active' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                            }>
                              {entry.hoursWorked ? `${entry.hoursWorked.toFixed(2)}h` : 'Active'}
                            </Badge>
                            <span className="text-xs text-gray-500 capitalize">
                              {entry.status?.replace('_', ' ')}
                            </span>
                          </div>
                        </div>

                        {entry.mileage && entry.mileage > 0 && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center mb-1">
                            <Car className="h-3 w-3 mr-1" />
                            {entry.mileage.toFixed(1)} miles
                          </p>
                        )}

                        {entry.notes && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 bg-gray-50 dark:bg-slate-800 p-2 rounded">
                            {entry.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payroll Tab */}
        {activeTab === 'payroll' && (
          <div className="p-4 space-y-4">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-800/20">
              <CardHeader>
                <CardTitle className="text-base flex items-center text-green-800 dark:text-green-100">
                  <DollarSign className="h-5 w-5 mr-2" />
                  Estimated Weekly Pay
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <p className="text-4xl font-bold text-green-900 dark:text-green-100">
                    ${stats.totalPay.toFixed(2)}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    This Week (Before Taxes)
                  </p>
                </div>

                <div className="space-y-2 border-t border-green-200 dark:border-green-800 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700 dark:text-green-300">Regular Hours ({stats.regularHours.toFixed(1)}h × ${hourlyRate})</span>
                    <span className="font-medium text-green-900 dark:text-green-100">${stats.regularPay.toFixed(2)}</span>
                  </div>
                  {stats.overtimeHours > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700 dark:text-green-300">Overtime ({stats.overtimeHours.toFixed(1)}h × ${(hourlyRate * overtimeRate).toFixed(2)})</span>
                      <span className="font-medium text-green-900 dark:text-green-100">${stats.overtimePay.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700 dark:text-green-300">Mileage ({stats.weekMileage.toFixed(1)} mi × ${mileageRate})</span>
                    <span className="font-medium text-green-900 dark:text-green-100">${stats.mileagePay.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pay Period Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  This Week Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                  <span className="text-gray-600 dark:text-gray-400">Total Hours</span>
                  <span className="font-semibold">{stats.weekHours.toFixed(2)} hrs</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                  <span className="text-gray-600 dark:text-gray-400">Regular Hours</span>
                  <span className="font-semibold">{stats.regularHours.toFixed(2)} hrs</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                  <span className="text-gray-600 dark:text-gray-400">Overtime Hours</span>
                  <span className="font-semibold text-orange-600">{stats.overtimeHours.toFixed(2)} hrs</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                  <span className="text-gray-600 dark:text-gray-400">Total Mileage</span>
                  <span className="font-semibold">{stats.weekMileage.toFixed(1)} mi</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 dark:text-gray-400">Shifts Worked</span>
                  <span className="font-semibold">{stats.weekEntries}</span>
                </div>
              </CardContent>
            </Card>

            {/* Current Rates */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Your Rates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Hourly Rate</span>
                  <span className="font-semibold">${hourlyRate.toFixed(2)}/hr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Overtime Rate</span>
                  <span className="font-semibold">${(hourlyRate * overtimeRate).toFixed(2)}/hr ({overtimeRate}x)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Mileage Rate</span>
                  <span className="font-semibold">${mileageRate.toFixed(2)}/mile</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">OT Threshold</span>
                  <span className="font-semibold">{overtimeThreshold} hrs/week</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="p-4 space-y-4">
            {/* Work Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  Work Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Set your work schedule to receive clock in/out reminders.
                </p>
                {dayNames.map((day, index) => (
                  <div key={day} className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={workSchedule[index]?.enabled || false}
                      onChange={(e) => setWorkSchedule(prev => ({
                        ...prev,
                        [index]: { ...prev[index], enabled: e.target.checked }
                      }))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="w-24 text-sm font-medium">{day}</span>
                    <Input
                      type="time"
                      value={workSchedule[index]?.start || ''}
                      onChange={(e) => setWorkSchedule(prev => ({
                        ...prev,
                        [index]: { ...prev[index], start: e.target.value }
                      }))}
                      disabled={!workSchedule[index]?.enabled}
                      className="w-28 h-8 text-xs"
                    />
                    <span className="text-gray-400">to</span>
                    <Input
                      type="time"
                      value={workSchedule[index]?.end || ''}
                      onChange={(e) => setWorkSchedule(prev => ({
                        ...prev,
                        [index]: { ...prev[index], end: e.target.value }
                      }))}
                      disabled={!workSchedule[index]?.enabled}
                      className="w-28 h-8 text-xs"
                    />
                  </div>
                ))}
                <Button
                  onClick={saveWorkSchedule}
                  className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Schedule
                </Button>
              </CardContent>
            </Card>

            {/* Payroll Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Payroll Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Hourly Rate ($)
                  </label>
                  <Input
                    type="number"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                    step="0.25"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Overtime Multiplier
                  </label>
                  <Input
                    type="number"
                    value={overtimeRate}
                    onChange={(e) => setOvertimeRate(parseFloat(e.target.value) || 1.5)}
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Overtime Threshold (hours/week)
                  </label>
                  <Input
                    type="number"
                    value={overtimeThreshold}
                    onChange={(e) => setOvertimeThreshold(parseInt(e.target.value) || 40)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Mileage Rate ($/mile)
                  </label>
                  <Input
                    type="number"
                    value={mileageRate}
                    onChange={(e) => setMileageRate(parseFloat(e.target.value) || 0)}
                    step="0.01"
                  />
                </div>
                <Button onClick={savePayrollSettings} className="w-full">
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </Button>
              </CardContent>
            </Card>

            {/* GPS Info */}
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">GPS Verification</h3>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  All clock in/out times are verified with GPS location to ensure accurate field reporting.
                  This helps with compliance and prevents timesheet disputes.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}